import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageHero from '../components/PageHero';
import ProductFilters from '../components/ProductFilters';
import Spinner from '../components/Spinner';
import ImageWithLoader from '../components/ImageWithLoader';
import './ShopPage.css';

const defaultFilters = {
  search: '',
  categories: [],
  price: { min: '', max: '' },
  sizes: [],
  inStockOnly: false
};

const parseListParam = (value) => (
  value ? value.split(',').map(item => item.trim()).filter(Boolean) : []
);

const parseShopState = (searchParams) => {
  const page = parseInt(searchParams.get('page'), 10);
  const perPage = parseInt(searchParams.get('per_page'), 10);

  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    perPage: [8, 12, 16, 24, 48].includes(perPage) ? perPage : 12,
    filters: {
      search: searchParams.get('search') || '',
      categories: parseListParam(searchParams.get('categories')),
      price: {
        min: searchParams.get('min_price') || '',
        max: searchParams.get('max_price') || ''
      },
      sizes: parseListParam(searchParams.get('sizes')),
      inStockOnly: searchParams.get('in_stock') === '1'
    }
  };
};

function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialShopState = parseShopState(searchParams);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialShopState.page);
  const [productsPerPage, setProductsPerPage] = useState(initialShopState.perPage);
  const [paginationSlots, setPaginationSlots] = useState(9);
  const [filters, setFilters] = useState(initialShopState.filters);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  useEffect(() => {
    const updatePaginationSlots = () => {
      const width = window.innerWidth;

      if (width <= 360) {
        setPaginationSlots(3);
      } else if (width <= 480) {
        setPaginationSlots(4);
      } else if (width <= 640) {
        setPaginationSlots(5);
      } else if (width <= 900) {
        setPaginationSlots(7);
      } else {
        setPaginationSlots(9);
      }
    };

    updatePaginationSlots();
    window.addEventListener('resize', updatePaginationSlots);

    return () => window.removeEventListener('resize', updatePaginationSlots);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allProducts]);

  useEffect(() => {
    const parsedState = parseShopState(searchParams);

    setCurrentPage(parsedState.page);
    setProductsPerPage(parsedState.perPage);
    setFilters(parsedState.filters);
  }, [searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (currentPage > 1) nextParams.set('page', String(currentPage));
    if (productsPerPage !== 12) nextParams.set('per_page', String(productsPerPage));
    if (filters.search) nextParams.set('search', filters.search);
    if (filters.categories?.length) nextParams.set('categories', filters.categories.join(','));
    if (filters.price?.min) nextParams.set('min_price', filters.price.min);
    if (filters.price?.max) nextParams.set('max_price', filters.price.max);
    if (filters.sizes?.length) nextParams.set('sizes', filters.sizes.join(','));
    if (filters.inStockOnly) nextParams.set('in_stock', '1');

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [currentPage, productsPerPage, filters, searchParams, setSearchParams]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      // Failed to load categories - not critical
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setAllProducts(response.data);
      setFilteredProducts(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allProducts];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const searchableText = [
          p.name,
          p.description,
          p.short_description,
          p.tags,
          p.category,
          p.printify_tags
        ].filter(Boolean).join(' ').toLowerCase();

        return searchableText.includes(searchLower);
      });
    }

    // Category filter
    if (filters.categories && filters.categories.length > 0) {
      filtered = filtered.filter(p => {
        if (!p.category) return false;
        const productCategories = p.category.split(',').map(c => c.trim());
        return filters.categories.some(fc => productCategories.includes(fc));
      });
    }

    // Price filter
    if (filters.price.min) {
      filtered = filtered.filter(p => p.price >= parseFloat(filters.price.min));
    }
    if (filters.price.max) {
      filtered = filtered.filter(p => p.price <= parseFloat(filters.price.max));
    }

    // Stock filter
    if (filters.inStockOnly) {
      filtered = filtered.filter(p => p.stock_status === 'instock');
    }

    // Size filter - this requires checking if product has any variation with the selected size
    if (filters.sizes && filters.sizes.length > 0) {
      filtered = filtered.filter(p => {
        // For now, we'll skip size filtering since we don't have variations loaded
        // This can be enhanced by loading variations from the API
        return true;
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);

    if (filterType === 'reset') {
      setFilters(prev => ({
        ...defaultFilters,
        search: prev.search
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    }
  };

  const handleSearchChange = (value) => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      search: value
    }));
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setProductsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getVisiblePageNumbers = () => {
    if (totalPages <= paginationSlots) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const middleSlots = Math.max(paginationSlots - 2, 1);
    let start = currentPage - Math.floor(middleSlots / 2);
    let end = start + middleSlots - 1;

    if (start < 2) {
      start = 2;
      end = start + middleSlots - 1;
    }

    if (end > totalPages - 1) {
      end = totalPages - 1;
      start = Math.max(2, end - middleSlots + 1);
    }

    const pages = [1];

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    pages.push(totalPages);

    return pages;
  };

  const renderPagination = (position) => {
    if (totalPages <= 1) return null;

    const visiblePageNumbers = getVisiblePageNumbers();

    return (
      <div className={`pagination pagination-${position}`}>
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          className="page-boundary"
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          &lt;
        </button>

        {visiblePageNumbers.map((number, index) => (
          <React.Fragment key={number}>
            {index > 0 && number - visiblePageNumbers[index - 1] > 1 && (
              <span className="page-gap" aria-hidden="true">...</span>
            )}
          <button
            type="button"
            onClick={() => handlePageChange(number)}
            className={`page-number ${currentPage === number ? 'active' : ''}`}
            aria-label={`Page ${number}`}
            aria-current={currentPage === number ? 'page' : undefined}
          >
            {number}
          </button>
          </React.Fragment>
        ))}

        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          className="page-boundary"
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          &gt;
        </button>
      </div>
    );
  };

  const renderSearchBox = (className) => (
    <div className={`shop-search-panel ${className}`}>
      <label htmlFor={`${className}-shop-search`}>Search Products</label>
      <div className="shop-search-control">
        <input
          id={`${className}-shop-search`}
          type="search"
          className="shop-search-input"
          placeholder="Search shirts..."
          value={filters.search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {filters.search && (
          <button
            type="button"
            className="shop-search-clear"
            onClick={() => handleSearchChange('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="shop-page">
      <PageHero title="SHOP" />

      <div className="products-container">
        <div className="content-with-sidebar">
          {/* Left Sidebar - Filters */}
          <aside className="sidebar">
            {renderSearchBox('desktop-shop-search')}
            <ProductFilters
              categories={categories}
              onFilterChange={handleFilterChange}
              activeFilters={filters}
            />
          </aside>

          {/* Main Content - Products */}
          <div className="main-content">
            {renderSearchBox('mobile-shop-search')}
            <div className="products-header">
              <p className="results-count">
                {loading ? 'Loading...' : `Showing ${indexOfFirstProduct + 1}-${Math.min(indexOfLastProduct, filteredProducts.length)} of ${filteredProducts.length} results`}
              </p>
              <div className="filters">
                <select className="filter-dropdown" value={productsPerPage} onChange={handleItemsPerPageChange} disabled={loading}>
                  <option value={8}>Show 8</option>
                  <option value={12}>Show 12</option>
                  <option value={16}>Show 16</option>
                  <option value={24}>Show 24</option>
                  <option value={48}>Show 48</option>
                </select>
              </div>
            </div>

            {loading ? (
              <Spinner text="Loading products..." />
            ) : error ? (
              <div className="error">Error: {error}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="no-products">No products found</div>
            ) : (
          <>
            {renderPagination('top')}

            <ul className="products columns-4">
              {currentProducts.map(product => {
              const imageUrl = product.images && product.images.length > 0
                ? product.images[0]
                : '/placeholder.png';

              return (
                <li key={product.id} className="product type-product status-publish instock">
                  <div className="product-block">
                    <div className="product-img-wrap fade">
                      <div className="inner">
                        <div className="product-image">
                          <ImageWithLoader src={imageUrl} alt={product.name} />
                          <div className="product-overlay">
                            <span className="view-product">VIEW PRODUCT</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Link to={`/product/${product.slug}`} className="woocommerce-LoopProduct-link"></Link>
                    <div className="caption">
                      <div className="group-label">
                        <span className="stock-label">In Stock</span>
                      </div>
                      <h3 className="woocommerce-loop-product__title">
                        <Link to={`/product/${product.slug}`}>{product.name}</Link>
                      </h3>
                      <div className="posted_in">
                        <a href="#" rel="tag">T-shirts</a>
                      </div>
                      <span className="price">
                        <span className="woocommerce-Price-amount amount">
                          <bdi><span className="woocommerce-Price-currencySymbol">$</span>{product.price?.toFixed(2)}</bdi>
                        </span>
                      </span>
                      <Link to={`/product/${product.slug}`} className="button add_to_cart_button">
                        SELECT OPTIONS
                      </Link>
                    </div>
                  </div>
                </li>
              );
            })}
            </ul>

            {renderPagination('bottom')}
          </>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShopPage;
